import { PropsWithChildren, ReactNode } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Popover as RadixPopover } from "radix-ui";
import { X } from "lucide-react";

export type PopoverProps = PropsWithChildren<{
  trigger: ReactNode;
  contentProps?: React.ComponentProps<typeof RadixPopover.Content>;
  triggerProps?: React.ComponentProps<typeof RadixPopover.Trigger>;
  rootProps?: React.ComponentProps<typeof RadixPopover.Root>;
  disabled?: boolean;
  tracking?: string;
  showClose?: boolean;
  onClose?: () => void;
}>;

function PopoverComponent({
  trigger,
  children,
  contentProps,
  triggerProps,
  rootProps,
  disabled = false,
  tracking,
  showClose = false,
  onClose,
}: PopoverProps) {
  return (
    <RadixPopover.Root {...rootProps}>
      <RadixPopover.Trigger
        {...triggerProps}
        disabled={disabled}
        data-track={tracking}
        className={twMerge(
          clsx(
            "inline-flex cursor-pointer items-center outline-none",
            disabled && "cursor-not-allowed opacity-50",
            triggerProps?.className,
          ),
        )}
      >
        {trigger}
      </RadixPopover.Trigger>

      <RadixPopover.Portal>
        <RadixPopover.Content
          sideOffset={5}
          {...contentProps}
          className={twMerge(
            clsx(
              "data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn",
              "bg-surface-default shadow-elevation-200 border-borderSubtle rounded-lg border p-4",
              "z-popover max-w-sm",
              "focus:outline-none",
              contentProps?.className,
            ),
          )}
        >
          {showClose && (
            <RadixPopover.Close
              aria-label="Close"
              onClick={onClose}
              className="text-iconMinimal hover:bg-surface-hover hover:text-textTertiary absolute top-2 right-2 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full"
            >
              <X />
            </RadixPopover.Close>
          )}
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

export const Popover = Object.assign(PopoverComponent, {
  Root: RadixPopover.Root,
  Trigger: RadixPopover.Trigger,
  Content: RadixPopover.Content,
  Portal: RadixPopover.Portal,
  Anchor: RadixPopover.Anchor,
  Close: RadixPopover.Close,
  Arrow: RadixPopover.Arrow,
});
